// Auth context — login, logout, session user. Access token stored in-memory (ADR auth).
// Wrap the app in <AuthProvider> and consume with useAuth() in any component.

import {
  createContext, useContext, useState, useCallback,
  type ReactNode, type JSX,
} from "react";
import { apiFetch, setAccessToken, getAccessToken, ApiError } from "../lib/api.js";

const SESSION_USER_KEY = "hcm_user";

export interface SessionUser {
  id:        string;
  name?:     string;
  email:     string;
  role:      string;
  tenant_id: string;
  // True after invite or admin-driven reset; the LoginPage redirects to
  // /change-password until the user clears the flag.
  must_change_password?: boolean;
}

interface LoginResponse {
  access_token: string;
  user:         SessionUser;
}

// Thrown when the API requires a TOTP token to complete login.
export class MfaRequiredError extends Error {
  constructor() { super("mfa_required"); this.name = "MfaRequiredError"; }
}

// Thrown when the user has never set up MFA (agency_admin / super_admin only).
export class MfaSetupRequiredError extends Error {
  constructor() { super("mfa_setup_required"); this.name = "MfaSetupRequiredError"; }
}

interface AuthContextValue {
  user:      SessionUser | null;
  isLoading: boolean;
  login:     (email: string, password: string, totpToken?: string) => Promise<void>;
  logout:    () => Promise<void>;
  // Re-read sessionStorage and re-derive auth state. Used by flows that
  // mint tokens outside the standard login path (e.g. MFA bootstrap-verify)
  // so the provider picks up the new session without a full page reload.
  refreshFromStorage: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<SessionUser | null>(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_USER_KEY);
      if (stored && getAccessToken()) return JSON.parse(stored) as SessionUser;
    } catch { /* ignore parse errors */ }
    return null;
  });
  const [isLoading, setIsLoading] = useState(false);

  const login = useCallback(async (email: string, password: string, totpToken?: string): Promise<void> => {
    setIsLoading(true);
    try {
      const payload: Record<string, string> = { email, password };
      if (totpToken) payload["totp_token"] = totpToken;
      const res = await apiFetch<LoginResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setAccessToken(res.access_token);
      sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(res.user));
      setUser(res.user);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as Record<string, unknown> | null;
        if (body?.["mfa_required"] === true)       throw new MfaRequiredError();
        if (body?.["mfa_setup_required"] === true)  throw new MfaSetupRequiredError();
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshFromStorage = useCallback((): void => {
    try {
      const stored = sessionStorage.getItem(SESSION_USER_KEY);
      if (stored && getAccessToken()) {
        setUser(JSON.parse(stored) as SessionUser);
        return;
      }
    } catch { /* ignore parse errors */ }
    setUser(null);
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiFetch("/auth/logout", { method: "POST" });
    } catch {
      // best-effort — clear local state regardless
    }
    setAccessToken(null);
    sessionStorage.removeItem(SESSION_USER_KEY);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, refreshFromStorage }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
